import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Check,
  Loader2,
  Search,
  MessageCircle,
  UserCheck,
  AlertTriangle,
} from 'lucide-react'
import {
  createCommercial,
  searchIdentityUsers,
  updateCommercial,
  type AdminCommercial,
  type IdentityCandidate,
} from '@/features/commercials/commercials'

interface CommercialEditorProps {
  /** null = alta nueva (con buscador); si viene, se edita ese comercial. */
  editing: AdminCommercial | null
  onCancel: () => void
  onSaved: () => void
}

function initials(name: string) {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join('') || '?'
  )
}

/**
 * Alta/edición de un comercial como REFERENCIA a un usuario de Identidad.
 * - Alta: se busca a la persona en Identidad (por email/nombre de usuario) y se
 *   guarda solo su `sub`; nombre/foto/teléfono salen de su perfil SSO.
 * - Edición: la persona no cambia; solo se ajusta la visibilidad y el WhatsApp
 *   alternativo. El resto es de solo lectura (viene vivo de Identidad).
 */
export function CommercialEditor({ editing, onCancel, onSaved }: CommercialEditorProps) {
  const isEdit = editing !== null

  // Persona elegida (alta) o derivada del comercial en edición.
  const [selected, setSelected] = useState<IdentityCandidate | null>(
    editing
      ? {
          sub: editing.sub ?? '',
          firstName: '',
          lastName: '',
          name: editing.name,
          email: '',
          username: '',
          photo: editing.photo,
          phoneNumber: editing.whatsapp,
        }
      : null,
  )

  const [active, setActive] = useState(editing?.active ?? true)
  const [override, setOverride] = useState(editing?.whatsappOverride ?? '')
  const [saving, setSaving] = useState(false)

  // Buscador de Identidad (solo en alta, hasta elegir persona).
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [candidates, setCandidates] = useState<IdentityCandidate[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    if (isEdit || selected) return
    const term = debounced.trim()
    if (term.length < 2) {
      setCandidates([])
      return
    }
    let alive = true
    setSearching(true)
    searchIdentityUsers(term)
      .then((items) => alive && setCandidates(items))
      .catch(() => alive && toast.error('No se pudo buscar en Identidad'))
      .finally(() => alive && setSearching(false))
    return () => {
      alive = false
    }
  }, [debounced, isEdit, selected])

  const effectiveWhatsapp = useMemo(() => {
    const clean = override.replace(/\D/g, '')
    return clean || selected?.phoneNumber || ''
  }, [override, selected])

  const save = async () => {
    if (!selected?.sub) return
    setSaving(true)
    try {
      if (isEdit && editing) {
        await updateCommercial(editing.id, { active, whatsappOverride: override })
      } else {
        await createCommercial({ sub: selected.sub, active, whatsappOverride: override })
      }
      onSaved()
    } catch {
      toast.error('No se pudo guardar el comercial')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-5 pb-24">
      <button
        onClick={onCancel}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" /> Todos los comerciales
      </button>
      <h1 className="font-serif text-[28px] font-normal tracking-tight text-ink">
        {isEdit ? 'Editar comercial' : 'Nuevo comercial'}
      </h1>

      <div className="max-w-md space-y-4">
        {/* Paso 1 (solo alta): buscar a la persona en Identidad */}
        {!isEdit && !selected && (
          <div className="space-y-3">
            <p className="text-sm text-ink-muted">
              Busca a la persona por su correo o usuario de la plataforma. El nombre, la
              foto y el teléfono se toman de su perfil y se mantienen al día solos.
            </p>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Correo o usuario en Identidad…"
                className="w-full rounded-lg border border-line bg-white py-2 pl-9 pr-9 text-sm text-ink outline-none focus:border-brand"
              />
              {searching && (
                <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-ink-subtle" />
              )}
            </div>

            {debounced.trim().length >= 2 && !searching && candidates.length === 0 && (
              <p className="rounded-lg border border-dashed border-line bg-panel p-4 text-center text-xs text-ink-muted">
                No se encontró ningún usuario. El comercial debe tener cuenta en la
                plataforma para autogestionarse.
              </p>
            )}

            {candidates.length > 0 && (
              <ul className="space-y-1">
                {candidates.map((c) => (
                  <li key={c.sub}>
                    <button
                      onClick={() => setSelected(c)}
                      className="flex w-full items-center gap-3 rounded-xl border border-line bg-panel p-2.5 text-left hover:border-brand hover:bg-surface"
                    >
                      {c.photo ? (
                        <img src={c.photo} alt="" className="size-10 shrink-0 rounded-full object-cover" />
                      ) : (
                        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-soft text-xs font-bold text-brand">
                          {initials(c.name)}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink">{c.name}</p>
                        <p className="truncate text-xs text-ink-muted">{c.email}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Paso 2: perfil resuelto (solo lectura) + ajustes del admin */}
        {selected && (
          <>
            <div className="flex items-center gap-3 rounded-card border border-line bg-panel p-3">
              {selected.photo ? (
                <img src={selected.photo} alt="" className="size-14 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="grid size-14 shrink-0 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand">
                  {initials(selected.name)}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-brand">
                  <UserCheck className="size-3.5" /> Perfil de Identidad
                </div>
                <p className="truncate text-sm font-semibold text-ink">{selected.name || 'Sin nombre'}</p>
                {selected.email && <p className="truncate text-xs text-ink-muted">{selected.email}</p>}
              </div>
              {!isEdit && (
                <button
                  onClick={() => {
                    setSelected(null)
                    setQuery('')
                    setCandidates([])
                  }}
                  className="text-xs font-medium text-ink-muted hover:text-ink"
                >
                  Cambiar
                </button>
              )}
            </div>

            {isEdit && editing?.unresolved && (
              <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" /> Identidad no ha podido
                refrescar este perfil todavía. Se muestra el último dato conocido.
              </p>
            )}

            <label className="block">
              <span className="text-xs font-semibold text-ink-muted">
                WhatsApp alternativo (opcional)
              </span>
              <input
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                placeholder={selected.phoneNumber || 'Sin teléfono en Identidad'}
                className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand"
              />
              <span className="mt-1 flex items-center gap-1 text-[11px] text-ink-subtle">
                <MessageCircle className="size-3" />
                {override.replace(/\D/g, '')
                  ? `Se usará ${effectiveWhatsapp}`
                  : selected.phoneNumber
                    ? `Sin override se usa el teléfono de Identidad (${selected.phoneNumber})`
                    : 'El perfil de Identidad no tiene teléfono; fija uno aquí.'}
              </span>
            </label>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="size-4 accent-[color:var(--brand-orange)]"
              />
              Activo (visible en el sitio)
            </label>
          </>
        )}
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-panel/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <button onClick={onCancel} className="text-sm font-medium text-ink-muted hover:text-ink">
            Cancelar
          </button>
          <button
            onClick={() => void save()}
            disabled={saving || !selected?.sub}
            className="ml-auto inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
            {saving ? 'Guardando…' : 'Guardar comercial'}
          </button>
        </div>
      </div>
    </div>
  )
}
